import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import {
  LayoutDashboard, Building2, Zap, Settings, LogOut, Menu, Sun, Moon,
  Wifi, Clock, ClipboardList, BarChart2, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import useNotification from '@/hooks/useNotification';
import useSiteSettings from '@/hooks/useSiteSettings';

const ALL_NAV = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin','operator'] },
  { path: '/rooms', icon: Building2, label: 'Odalar', roles: ['admin','operator'] },
  { path: '/relays', icon: Zap, label: 'Röle Paneli', roles: ['admin','operator'] },
  { path: '/schedules', icon: Clock, label: 'Zamanlayıcı', roles: ['admin','operator'] },
  { path: '/analytics', icon: BarChart2, label: 'Analitik', roles: ['admin','operator'] },
  { path: '/logs', icon: ClipboardList, label: 'Aktivite Logu', roles: ['admin'] },
  { path: '/users', icon: Users, label: 'Kullanıcılar', roles: ['admin'] },
  { path: '/settings', icon: Settings, label: 'Ayarlar', roles: ['admin'] },
];

const SidebarContent = ({ onClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'admin';
  const NAV_ITEMS = ALL_NAV.filter(n => n.roles.includes(role));
  const siteSettings = useSiteSettings();

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: siteSettings.theme_color || '#2563EB' }}>
            {siteSettings.logo_url
              ? <img src={siteSettings.logo_url} className="w-6 h-6 object-contain" alt="logo"
                  onError={e => { e.target.style.display='none'; }} />
              : <Wifi className="w-5 h-5 text-white" />}
          </div>
          <div>
            <p className="font-bold text-sm leading-none">{siteSettings.site_title || 'Smart Office'}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Otomasyon Paneli</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV_ITEMS.map(({ path, icon: Icon, label }) => {
          const active = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              onClick={onClose}
              data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <Icon className="w-4.5 h-4.5 shrink-0" size={18} />
              {label}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-border space-y-1">
        <div className="px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground">Giriş yapıldı</p>
          <p className="text-sm font-semibold">{user?.username}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          Çıkış Yap
        </Button>
      </div>
    </div>
  );
};

const ThemeToggle = ({ testId = "theme-toggle-btn" }) => {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      data-testid={testId}
      aria-label="Tema değiştir"
      className="h-9 w-9"
    >
      <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
    </Button>
  );
};

const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  useNotification(); // ESP offline bildirimi

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col fixed inset-y-0 left-0 border-r border-border bg-card z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-card flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SidebarContent onClose={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Wifi className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-sm">Smart Office</span>
          </div>
        </div>
        <ThemeToggle testId="theme-toggle-mobile-btn" />
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 mt-14 md:mt-0 overflow-auto">
        {/* Desktop theme toggle in top-right */}
        <div className="hidden md:flex absolute top-4 right-4 z-30">
        <ThemeToggle testId="theme-toggle-desktop-btn" />
        </div>
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
